/**
 * Field Substitution Utilities
 * Used for replacing field placeholders like {workflow} with actual row values
 */

/**
 * Field mapping from display names to API field names
 */
export const FIELD_NAME_MAP = {
  'project': 'project',
  'target': 'target',
  'mutation': 'mutation',
  'subgroup': 'subgroup',
  'workflow': 'prep_workflow',
  'batch': 'batch',
  'ligand': 'ligand_name',
  'ligand_name': 'ligand_name',
  'ligand_tag': 'ligand_tag',
  'author': 'author',
  'notes': 'notes',
  'origin': 'origin_name',
  'origin_name': 'origin_name',
  'prepared': 'prepared_at',
  'prepared_at': 'prepared_at',
};

/**
 * Get display-friendly field names for UI
 */
export const DISPLAY_FIELDS = [
  { name: 'project', label: 'Project' },
  { name: 'target', label: 'Target' },
  { name: 'mutation', label: 'Mutation' },
  { name: 'subgroup', label: 'Subgroup' },
  { name: 'workflow', label: 'Workflow' },
  { name: 'batch', label: 'Batch' },
  { name: 'ligand_name', label: 'Ligand Name' },
  { name: 'ligand_tag', label: 'Ligand Tag' },
  { name: 'author', label: 'Author' },
  { name: 'notes', label: 'Notes' },
  { name: 'origin_name', label: 'Origin Name' },
];

/**
 * Substitute field placeholders in text with actual row values
 *
 * @param {string} text - Text containing placeholders like {field_name}
 * @param {object} rowData - Row data object with field values
 * @param {object} options - Configuration options
 * @param {boolean} options.preserveEmptyPlaceholders - Keep placeholder if value is empty
 * @param {string} options.emptyValue - Default value for empty fields
 * @returns {string} Text with placeholders replaced by actual values
 */
export const substituteFieldPlaceholders = (text, rowData, options = {}) => {
  const {
    preserveEmptyPlaceholders = false,
    emptyValue = ''
  } = options;

  if (!text || !rowData) {
    return text || '';
  }

  // Handle escaped braces first - store them temporarily
  let processedText = text
    .replace(/\\\{/g, '__ESCAPED_OPEN_BRACE__')
    .replace(/\\\}/g, '__ESCAPED_CLOSE_BRACE__');

  // Replace field placeholders
  processedText = processedText.replace(/\{([^}]+)\}/g, (match, fieldName) => {
    // Remove any whitespace from field name
    const cleanFieldName = fieldName.trim().toLowerCase();

    // Map display name to API field name
    const apiField = FIELD_NAME_MAP[cleanFieldName];

    // If no mapping found, try using the field name directly
    const fieldToUse = apiField || cleanFieldName;

    // Get the value from row data
    let value = rowData[fieldToUse];

    // Handle workflow field specially - check both prep_workflow and workflow
    if (cleanFieldName === 'workflow' && !value) {
      value = rowData.workflow || rowData.prep_workflow;
    }

    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      if (preserveEmptyPlaceholders) {
        return match; // Keep the original placeholder
      }
      return emptyValue; // Return empty value
    }

    // Convert to string and return
    return String(value);
  });

  // Restore escaped braces as literal braces
  processedText = processedText
    .replace(/__ESCAPED_OPEN_BRACE__/g, '{')
    .replace(/__ESCAPED_CLOSE_BRACE__/g, '}');

  return processedText;
};

/**
 * Extract field names from a text containing placeholders
 *
 * @param {string} text - Text containing placeholders
 * @returns {string[]} Array of field names found in the text
 */
export const extractFieldsFromText = (text) => {
  if (!text) return [];

  const fields = [];
  const regex = /\{([^}]+)\}/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const fieldName = match[1].trim().toLowerCase();
    if (!fields.includes(fieldName)) {
      fields.push(fieldName);
    }
  }

  return fields;
};

/**
 * Validate if all field placeholders in text are valid
 *
 * @param {string} text - Text containing placeholders
 * @returns {object} Validation result with isValid flag and invalid fields
 */
export const validateFieldPlaceholders = (text) => {
  const fields = extractFieldsFromText(text);
  const invalidFields = [];

  fields.forEach(field => {
    if (!FIELD_NAME_MAP[field] && !FIELD_NAME_MAP[field.toLowerCase()]) {
      invalidFields.push(field);
    }
  });

  return {
    isValid: invalidFields.length === 0,
    invalidFields
  };
};

/**
 * Get sample replacement text with field values from a row
 *
 * @param {string} replaceText - Replacement text with placeholders
 * @param {object} sampleRow - Sample row to get values from
 * @returns {string} Sample text with actual values
 */
export const getSampleReplacement = (replaceText, sampleRow) => {
  if (!replaceText || !sampleRow) return replaceText || '';

  // First substitute field placeholders
  let result = substituteFieldPlaceholders(replaceText, sampleRow, { emptyValue: '<empty>' });

  // Then handle regex capture groups - show them as-is for sample
  // since we need the actual regex match to replace them
  result = result.replace(/\$(\d+)/g, (match) => match);

  return result;
};